import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from '../api/client';
import type { ApiResponse } from '../types';

export type FiscalYearLock = {
  fiscalYear: number;
  lockedAt: string;
  lockedReason: string | null;
  declarationStatus: 'not_filed' | 'csv_exported' | 'filed';
  csvExportedAt: string | null;
  filedAt: string | null;
};

export type PrecheckResult = {
  fiscalYear: number;
  canLock: boolean;
  blockers: string[];
  details: {
    entryCount: number;
    unlinkedDocsCount: number;
    unbalancedEntries: Array<{ entryId: string; entryDate: string; description: string; debit: number; credit: number }>;
    accountsWithoutNtaCode: Array<{ id: string; code: string; name: string; ntaCategoryCode: string | null }>;
  };
};

export function useFiscalYearLocks() {
  return useQuery({
    queryKey: ['fiscal-year-locks'],
    queryFn: async () => {
      const res = await api.get<ApiResponse<FiscalYearLock[]>>('/api/fiscal-year-locks');
      return res.data;
    },
  });
}

export function usePrecheck(fiscalYear: number | undefined) {
  return useQuery({
    queryKey: ['fiscal-year-precheck', fiscalYear],
    enabled: fiscalYear !== undefined,
    queryFn: async () => {
      const res = await api.get<ApiResponse<PrecheckResult>>(
        `/api/fiscal-year-locks/${fiscalYear}/precheck`,
      );
      return res.data;
    },
  });
}

export function useLockFiscalYear() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ fiscalYear, reason }: { fiscalYear: number; reason?: string }) =>
      api.post<ApiResponse<FiscalYearLock>>('/api/fiscal-year-locks', { fiscalYear, reason }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['fiscal-year-locks'] }),
  });
}

export function useUnlockFiscalYear() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (fiscalYear: number) => api.delete(`/api/fiscal-year-locks/${fiscalYear}`),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['fiscal-year-locks'] }),
  });
}

export function useUpdateDeclarationStatus() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({
      fiscalYear,
      declarationStatus,
      filedAt,
    }: {
      fiscalYear: number;
      declarationStatus: 'not_filed' | 'csv_exported' | 'filed';
      filedAt?: string;
    }) =>
      api.patch<ApiResponse<FiscalYearLock>>(`/api/fiscal-year-locks/${fiscalYear}/status`, {
        declarationStatus,
        filedAt,
      }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['fiscal-year-locks'] }),
  });
}
